export interface LoginCredentials {
  email: string;
  password: string;
}

export interface UserRegister {
  email: string;
  password: string;
  nome_completo: string;
  codigovinculacao: string;
  tipo: string;
}

// Tipos para o sistema de autenticação baseado na API /api/auth/profile/

// Tipos de usuário do sistema
export type TipoUsuario = "TACT" | "FRQ" | "ASN"; // Adicionado "ASN" para assinante

// Status de vínculo
export type StatusVinculo = "ATIVO" | "INATIVO" | "PENDENTE" | "REJEITADO";

// Cargos disponíveis na franquia
export type CargoFranquia =
  | "ADMIN"
  | "GESTOR"
  | "FINANCEIRO"
  | "CADASTRAMENTO"
  | "VENDEDOR";

// Status da membership
export type StatusMembership = "ATIVO" | "INATIVO" | "SUSPENSO";

// Gênero do usuário
export type Genero = "MASCULINO" | "FEMININO" | "OUTROS" | "NAO_INFORMADO";

// Interface para telefones
export interface Telefone {
  id: number;
  numero: string;
}

// Interface para membership da franquia
export interface FranquiaMembership {
  id_membro_franquia: number;
  franquia_id: number;
  franquia_nome: string;
  cargos: CargoFranquia[];
  status: StatusMembership;
}

// Interface para membership da assinante (empresa contratante)
export interface AssinanteMembership {
  id_membro_assinante: number;
  assinante_id: number;
  assinante_nome: string;
  cargo: string;
  status: StatusMembership;
}

// Interface deprecated - mantida para retrocompatibilidade
export interface EmpresaMembership {
  id_membro_empresa: number;
  empresa_id: number;
  empresa_nome: string;
  cargo: string;
  status: StatusMembership;
}

// Interface principal do usuário baseada na resposta da API
export interface User {
  id: number;
  email: string;
  nome_completo: string;
  cpf: string;
  data_nascimento: string | null;
  genero: Genero | null;
  telefones: Telefone[];
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  created: string;
  modified: string;
  last_login: string | null;
  codigovinculacao: string;
  status_vinculo: StatusVinculo;
  tipo: TipoUsuario;
  franquia_membership: FranquiaMembership | null;
  assinante_membership: AssinanteMembership | null;
  empresa_membership?: EmpresaMembership | null; // Deprecated - usar assinante_membership
}

export interface UserProfile {
  nome_completo?: string;
  cpf?: string;
  data_nascimento?: string;
  genero?: string;
}

export interface ChangePassword {
  old_password: string;
  password: string;
  password_confirm: string;
}

export interface TokenResponse {
  refresh: string;
  access: string;
}

export interface LoginResponse extends TokenResponse {
  user: User;
}

export interface AuthResponse {
  detail: string;
  token: TokenResponse;
}

export interface AuthState {
  user: User | null;
  token: TokenResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Enum para permissões do sistema
export enum Permission {
  // Permissões gerais
  VIEW_DASHBOARD = "VIEW_DASHBOARD",

  // Permissões de franquia
  MANAGE_FRANQUIA = "MANAGE_FRANQUIA",
  VIEW_FRANQUIA = "VIEW_FRANQUIA",

  // Permissões de usuários
  ADD_GESTOR = "ADD_GESTOR",
  ADD_FUNCIONARIO = "ADD_FUNCIONARIO",
  MANAGE_USERS = "MANAGE_USERS",
  VIEW_USERS = "VIEW_USERS",

  // Permissões financeiras
  VIEW_FINANCEIRO = "VIEW_FINANCEIRO",
  MANAGE_FINANCEIRO = "MANAGE_FINANCEIRO",

  // Permissões de cadastramento
  MANAGE_CADASTROS = "MANAGE_CADASTROS",
  VIEW_CADASTROS = "VIEW_CADASTROS",

  // Permissões de vendas
  MANAGE_VENDAS = "MANAGE_VENDAS",
  VIEW_VENDAS = "VIEW_VENDAS",

  // Permissões de empreendimentos
  MANAGE_EMPREENDIMENTOS = "MANAGE_EMPREENDIMENTOS",
  VIEW_EMPREENDIMENTOS = "VIEW_EMPREENDIMENTOS",

  // Permissões de sistema (TACT)
  MANAGE_SYSTEM = "MANAGE_SYSTEM",
  VIEW_ALL_FRANQUIAS = "VIEW_ALL_FRANQUIAS",
  MANAGE_CIDADES = "MANAGE_CIDADES",
  MANAGE_AREAS_CIDADE = "MANAGE_AREAS_CIDADE",
}

// Interface para o contexto de autenticação
export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  hasRole: (role: CargoFranquia) => boolean;
  isTactUser: () => boolean;
  isFranquiaUser: () => boolean;
  getFranquiaId: () => number | null;
  getFranquiaNome: () => string | null;
}

// Interface para resposta de erro da API
export interface AuthError {
  message: string;
  code?: string;
  details?: Record<string, string[]>;
}
